import * as React from "react";

import { useFileTransfer } from "@/providers/FileTransfer";
import type { SelectionOption } from "@/shared/components/ResourceView";
import type { FileRecord } from "@/shared/domain/files";
import { DownloadIcon } from "@/shared/foundation/icons/mui";

/**
 * A "Download" bulk action for a `<FilesList>` selection.
 *
 * The selection always leaves as one ZIP export, however few files it holds.
 * The export surfaces in the transfer toast with its progress, whereas
 * handing the browser several direct downloads at once trips its popup
 * blocker. The toast opens, and the selection clears, on the click itself;
 * a refused export is reported in the toast, so nothing is left for the
 * page to show.
 */
export function useDownloadFilesOption(): SelectionOption<FileRecord> {
  const { beginExport } = useFileTransfer();

  return React.useMemo<SelectionOption<FileRecord>>(
    () => ({
      name: "Download",
      icon: <DownloadIcon fontSize="small" />,
      action: ({ selected, clearSelection }) => {
        const label =
          selected.length === 1 ? selected[0].name : `${selected.length} files`;
        beginExport(
          selected.map((file) => file.file_id),
          label,
        );
        clearSelection();
      },
    }),
    [beginExport],
  );
}
